import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    cancelarAssinaturaClube,
    obterAssinantesClube,
    obterClubeEstabelecimento,
    obterMensalidadesAssinatura,
    salvarClubeEstabelecimento,
    type AssinanteClube,
    type MensalidadeClube,
    type ResumoFinanceiroClube,
    type StatusAssinaturaClube,
    type TotaisAssinantesClube,
} from "../../../services/clubeBeneficios"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import {
    ClubeBeneficiosInicial,
    modeloDoBackend,
    montarPayload,
    ordenarMensalidades,
    validarClube,
} from "../models/clubeBeneficiosModel"
import { ClubeBeneficiosActionsTypes } from "./clubeBeneficiosActions"
import { ClubeBeneficiosContext, TotaisAssinantesInicial } from "./ClubeBeneficiosContext"
import { clubeBeneficiosReducer } from "./clubeBeneficiosReducer"

export function ClubeBeneficiosContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(clubeBeneficiosReducer, ClubeBeneficiosInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const [assinantes, setAssinantes] = useState<AssinanteClube[]>([])
    const [totais, setTotais] = useState<TotaisAssinantesClube>(TotaisAssinantesInicial)
    const [resumoFinanceiro, setResumoFinanceiro] = useState<ResumoFinanceiroClube | null>(null)
    const [filtroStatus, setFiltroStatus] = useState<StatusAssinaturaClube | "">("")
    const [filtroClubeId, setFiltroClubeId] = useState<number | "">("")
    const [carregandoAssinantes, setCarregandoAssinantes] = useState(false)
    const [erroAssinantes, setErroAssinantes] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            const response = await obterClubeEstabelecimento(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar o Clube de Benefícios")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: ClubeBeneficiosActionsTypes.CARREGAR,
                    payload: modeloDoBackend(response),
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar o Clube de Benefícios")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        const erroValidacao = validarClube(dados)
        if (erroValidacao) throw new Error(erroValidacao)

        try {
            setLoading(true)
            const response = await salvarClubeEstabelecimento(autorizacao, montarPayload(dados))
            if ("retornoErro" in response) {
                throw new Error(response.mensagem || "Erro ao salvar o Clube de Benefícios")
            }
            dispatch({
                type: ClubeBeneficiosActionsTypes.CARREGAR,
                payload: modeloDoBackend(response),
            })
        } finally {
            setLoading(false)
        }
    }, [dados])

    const carregarAssinantes = useCallback(
        async (status?: StatusAssinaturaClube | "", clubeId?: number | "") => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
    
            const filtro = status ?? filtroStatus
            const plano = clubeId ?? filtroClubeId
            setFiltroStatus(filtro)
            setFiltroClubeId(plano)
            try {
                setCarregandoAssinantes(true)
                const response = await obterAssinantesClube(autorizacao, filtro, plano)
                if ("retornoErro" in response) {
                    setErroAssinantes(response.mensagem || "Erro ao carregar os assinantes")
                } else {
                    setErroAssinantes(null)
                    setAssinantes(response.assinantes ?? [])
                    setTotais({ ...TotaisAssinantesInicial, ...(response.totais ?? {}) })
                    setResumoFinanceiro(response.resumoFinanceiro ?? null)
                }
            } catch {
                setErroAssinantes("Erro ao carregar os assinantes")
            } finally {
                setCarregandoAssinantes(false)
            }
        },
        [filtroStatus, filtroClubeId]
    )

    const cancelarAssinatura = useCallback(
        async (idAssinatura: number, motivo: string) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return

            try {
                setLoading(true)
                const response = await cancelarAssinaturaClube(autorizacao, idAssinatura, motivo)
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao cancelar a assinatura")
                }
            } finally {
                setLoading(false)
            }
            await carregarAssinantes(filtroStatus, filtroClubeId)
        },
        [carregarAssinantes, filtroStatus, filtroClubeId]
    )

    const carregarMensalidades = useCallback(
        async (idAssinatura: number): Promise<MensalidadeClube[]> => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return []

            const response = await obterMensalidadesAssinatura(autorizacao, idAssinatura)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem || "Erro ao carregar as mensalidades")
            }
            return ordenarMensalidades(response.mensalidades ?? [])
        },
        []
    )

    return (
        <ClubeBeneficiosContext.Provider
            value={{
                dados,
                dispatch,
                salvar,
                erroCarregamento,
                assinantes,
                totais,
                resumoFinanceiro,
                filtroStatus,
                filtroClubeId,
                carregandoAssinantes,
                erroAssinantes,
                carregarAssinantes,
                cancelarAssinatura,
                carregarMensalidades,
            }}
        >
            <Loading />
            {children}
        </ClubeBeneficiosContext.Provider>
    )
}
